import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  AssistanceElementContentModel,
  TitleRteItemElementContentModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useClientTranslation } from '@/lib/core';
import { useAuthStore, useUIStore } from '@/lib/core/stores';
import { initializeTalkdeskChat, setTalkdeskCallbacks } from '@/lib/core/utils';
import { useTalkdeskIdentitySync } from '@/lib/core/hooks/useTalkdeskIdentitySync';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { ProfileContactUsAssistance } from '@/components/shared/profile/contact-us/assistance';
import { useApp } from '@/lib/core/providers/appProvider';

export const Assistance = (props: ModuleProperties) => {
  const { user } = useApp();
  const { user: authUser } = useAuthStore();
  const t = useClientTranslation();
  const content = props.content as AssistanceElementContentModel | undefined;
  const p = content?.properties;
  const supportPhone = props.context?.settings?.properties?.supportContactPhone as string | undefined;
  const supportEmail = authUser?.email as string | undefined;
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  const hasActiveMembership = user?.hasActiveMembership ?? false;

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  useEffect(() => {
    setTalkdeskCallbacks({
      onOpen: () => null,
      onClose: () => null,
    });
    return () => setTalkdeskCallbacks({});
  }, []);

  useTalkdeskIdentitySync();

  const faqList = (p?.faqList?.items ?? [])
    .map(i => i.content as TitleRteItemElementContentModel)
    .filter(i => i?.properties?.title);

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__assistance']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ProfileContactUsAssistance
        subTitle={p?.description}
        conciergeSectionTitle={
          hasActiveMembership ? p?.conciergeTitle : t('App.Labels.AssistanceCustomerServiceTitle')
        }
        conciergeDescription={p?.conciergeDescription}
        conciergeButtonLabel={
          hasActiveMembership ? p?.conciergeBtnLabel : t('App.Labels.AssistanceCustomerServiceButton')
        }
        onContactConcierge={() => initializeTalkdeskChat()}
        callUsSectionTitle={p?.callUsTitle}
        callUsSchedule={p?.callUsDescription}
        callUsButtonLabel={p?.callUsBtnLabel}
        onCallUs={
          supportPhone
            ? () => {
                const url = `tel:${supportPhone}`;
                if (Capacitor.isNativePlatform()) {
                  window.open(url, '_system');
                } else {
                  window.location.href = url;
                }
              }
            : undefined
        }
        onSendEmail={
          supportEmail
            ? () => {
                const url = `mailto:${supportEmail}?subject=${'Support Email'}&body=${'Need Support'}`;
                if (Capacitor.isNativePlatform()) {
                  window.open(url, '_system');
                } else {
                  window.location.href = url;
                }
              }
            : undefined
        }
        callbackButtonLabel={p?.requestACallbackBtnLabel}
        emailUsSectionTitle={p?.emailUsTitle}
        emailUsDescription={p?.emailUsDescription}
        emailUsButtonLabel={p?.emailUsBtnLabel}
        faqSectionTitle={p?.faqTitle}
        faqDescription={p?.faqDescription}
        faqList={faqList}
      />
    </ModuleWrapper>
  );
};
